import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  IconButton,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Header from './components/Header';
import Footer from './components/Footer';
import { useTranslation } from 'react-i18next';
import { BRAND_GRADIENTS } from '../../config/brand';

// Stil tanımlamaları
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  background: BRAND_GRADIENTS.vertical, // BRAND'den
  overflow: 'hidden', // Ana sayfanın kaydırmasını engelle
}));

const ScrollableWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto', // Sadece bu alanda kaydırma çubuğu olsun
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.5)',
    },
  },
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    margin: theme.spacing(1),
  },
}));


const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
})) as typeof Typography;


const TermsOfService: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();


  return (
    <PageContainer>
      <Helmet>
        <title>{t('termsOfService.title')}</title>
        <meta 
          name="description" 
          content={t('termsOfService.description')}
        />
      </Helmet>
      
      <Header />

      <ScrollableWrapper>
        <ContentContainer maxWidth="md">
          <ContentPaper>
            <Typography variant="h3" component="h1" gutterBottom align="center" color="primary" fontWeight="bold">
              {t('termsOfService.mainTitle')}
            </Typography>
            
            <Typography variant="body1" paragraph color="text.secondary" align="center">
              <strong>{t('termsOfService.lastUpdated')}</strong>
            </Typography>

            <Divider sx={{ my: 3 }} />

            <SectionTitle variant="h5" component="h2">
              {t('termsOfService.acceptance.title')}
            </SectionTitle>
            <Typography variant="body1" paragraph>
              {t('termsOfService.acceptance.content')}
            </Typography>

            <SectionTitle variant="h5" component="h2">
              {t('termsOfService.serviceDescription.title')}
            </SectionTitle>
            <Typography variant="body1" paragraph>
              {t('termsOfService.serviceDescription.content')}
            </Typography>

            <SectionTitle variant="h5" component="h2">
              {t('termsOfService.userResponsibilities.title')}
            </SectionTitle>
            <Typography variant="body1" paragraph>
              {t('termsOfService.userResponsibilities.intro')}
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
              <li>{t('termsOfService.userResponsibilities.list.item1')}</li>
              <li>{t('termsOfService.userResponsibilities.list.item2')}</li>
              <li>{t('termsOfService.userResponsibilities.list.item3')}</li>
              <li>{t('termsOfService.userResponsibilities.list.item4')}</li>
              <li>{t('termsOfService.userResponsibilities.list.item5')}</li>
            </Typography>

            <SectionTitle variant="h5" component="h2">
              {t('termsOfService.dataProtection.title')}
            </SectionTitle>
            <Typography variant="body1" paragraph>
              {t('termsOfService.dataProtection.content')}
            </Typography>

            <SectionTitle variant="h5" component="h2">
              {t('termsOfService.intellectualProperty.title')}
            </SectionTitle>
            <Typography variant="body1" paragraph>
              {t('termsOfService.intellectualProperty.content')}
            </Typography>

            <SectionTitle variant="h5" component="h2">
              {t('termsOfService.limitation.title')}
            </SectionTitle>
            <Typography variant="body1" paragraph>
              {t('termsOfService.limitation.content')}
            </Typography>

            <SectionTitle variant="h5" component="h2">
              {t('termsOfService.termination.title')}
            </SectionTitle>
            <Typography variant="body1" paragraph>
              {t('termsOfService.termination.content')}
            </Typography>

            <SectionTitle variant="h5" component="h2">
              {t('termsOfService.modifications.title')}
            </SectionTitle>
            <Typography variant="body1" paragraph>
              {t('termsOfService.modifications.content')}
            </Typography>

            <SectionTitle variant="h5" component="h2">
              {t('termsOfService.governingLaw.title')}
            </SectionTitle>
            <Typography variant="body1" paragraph>
              {t('termsOfService.governingLaw.content')}
            </Typography>

            <SectionTitle variant="h5" component="h2">
              {t('termsOfService.contact.title')}
            </SectionTitle>
            <Typography variant="body1" paragraph>
              {t('termsOfService.contact.content')}
            </Typography>
            <Typography variant="body1" component="div">
              <strong>MT Teknoloji</strong><br />
              Adres: Yeni Mahalle Saka sokak No:390 Merkez/Düzce<br />
              E-posta: <a href="mailto:info@mt-teknoloji.com" style={{ color: '#0D47A1' }}>info@mt-teknoloji.com</a><br />
              Telefon: +90 (312) 400 09 12
            </Typography>

            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/')}
                sx={{ px: 4, py: 1.5, borderRadius: 2 }}
              >
                {t('navigation.home')}
              </Button>
            </Box>
          </ContentPaper>
        </ContentContainer>
        <Footer />
      </ScrollableWrapper>
    </PageContainer>
  );
};

export default TermsOfService;